import mapboxgl from 'mapbox-gl';
import { MAP_STYLES, MapStyleKey } from './MapboxMap';
import { applySearchedLabel, styleUsesSearchedLabel } from './searchedLabel';
import { drawLocationBadge, styleUsesLocationBadge } from './locationBadge';
import { drawMapAttribution } from './mapAttribution';

// ---------------------------------------------------------------------------
// Render a map image on a canvas with Mapbox GL JS — the searched-location
// label baked in — and return it as a PNG Blob.
//
// Used for every preview surface (order sheet, AR texture, Stripe thumbnail,
// emails) and for the 60cm print. The Static Images API can't do the custom
// label and can't render the Suburbs style at all, so this is the only way.
//
// Framing matches the customer's live map: a square crop at their centre and
// zoom. Resolution is controlled by `size` via the device-pixel-ratio trick
// (Mapbox's documented print approach) — the geographic span is unchanged,
// only the pixel density. `size` 1024–1200 for previews, 7912 for a 60cm
// print (~335 DPI).
// ---------------------------------------------------------------------------

// The framing crop, in CSS px. Must stay 1024 to match the historical
// getStaticMapPreviewUrl(...,1024,1024) framing that Stripe/emails have used.
const FRAME_CSS = 1024;

export interface RenderMapImageOptions {
  coords: [number, number];
  zoom: number;
  styleKey: MapStyleKey;
  /** Searched place name. Rendered as the big label (Suburbs style only). */
  label?: string | null;
  /** Current map centre's name (follows panning, unlike `label`). Rendered
   *  as the small bottom badge (Streets style only). Falls back to `label`
   *  if not given. */
  badgeLabel?: string | null;
  /** Output edge length in px. Default 1024. */
  size?: number;
  /** Show the Mapbox/OSM attribution in a corner. Default false. */
  includeAttribution?: boolean;
  /** Output format. Default 'png' (lossless — for on-screen previews and the
   *  print). 'jpeg' for the Stripe/email upload, which has a 512KB limit. */
  format?: 'png' | 'jpeg';
  /** JPEG quality 0–1 (ignored for png). Default 0.9. */
  quality?: number;
  /** Abort a render that's no longer needed (e.g. React unmount). */
  signal?: AbortSignal;
}

const raf = () => new Promise<void>((r) => requestAnimationFrame(() => r()));
const wait = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

function throwIfAborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new DOMException('Render aborted', 'AbortError');
}

/** Wait for a map event once, or resolve after `timeoutMs` regardless. */
function onceOrTimeout(map: mapboxgl.Map, event: string, timeoutMs: number) {
  return new Promise<void>((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    map.once(event, finish);
    setTimeout(finish, timeoutMs);
  });
}

async function settle(map: mapboxgl.Map, signal?: AbortSignal) {
  await onceOrTimeout(map, 'idle', 20000);
  throwIfAborted(signal);
  // Labels/glyphs occasionally lag the first 'idle'.
  for (let i = 0; i < 30 && !map.areTilesLoaded(); i++) await wait(150);
  throwIfAborted(signal);
  await wait(700);
}

export async function renderMapImage({
  coords,
  zoom,
  styleKey,
  label,
  badgeLabel,
  size = FRAME_CSS,
  includeAttribution = false,
  format = 'png',
  quality = 0.9,
  signal,
}: RenderMapImageOptions): Promise<Blob> {
  throwIfAborted(signal);

  const dpr = size / FRAME_CSS;

  const host = document.createElement('div');
  host.style.cssText =
    `position:fixed;top:0;left:0;width:${FRAME_CSS}px;height:${FRAME_CSS}px;` +
    'opacity:0;pointer-events:none;z-index:-1;';
  document.body.appendChild(host);

  // Mapbox reads devicePixelRatio at construction; override it so the drawing
  // buffer comes out at `size` with the same geographic span. Restored in
  // finally. The live map won't re-read it unless it resizes in this window.
  const dprDescriptor = Object.getOwnPropertyDescriptor(window, 'devicePixelRatio');
  Object.defineProperty(window, 'devicePixelRatio', {
    configurable: true,
    get: () => dpr,
  });

  let map: mapboxgl.Map | undefined;
  try {
    map = new mapboxgl.Map({
      container: host,
      style: MAP_STYLES[styleKey].url,
      center: coords,
      zoom,
      interactive: false,
      preserveDrawingBuffer: true,
      fadeDuration: 0,
      attributionControl: includeAttribution,
      trackResize: false,
    });

    await onceOrTimeout(map, 'load', 25000);
    throwIfAborted(signal);

    if (label && styleUsesSearchedLabel(styleKey)) {
      applySearchedLabel(map, { name: label, coords });
    }

    await settle(map, signal);

    const gl = map.getCanvas();
    // Normalise to an exact size×size PNG regardless of GL's rounding.
    const out = document.createElement('canvas');
    out.width = size;
    out.height = size;
    const ctx = out.getContext('2d');
    if (!ctx) throw new Error('2D context unavailable');
    ctx.drawImage(gl, 0, 0, size, size);

    // Attribution draws on every render, both styles — it's the only thing
    // carrying the required Mapbox/OSM credit now that the GL map's own
    // attributionControl is off. Drawn first so its reserved bottom space
    // is known before the Streets caption stacks above it.
    const attribution = await drawMapAttribution(ctx, size);

    const badgeText = badgeLabel ?? label;
    if (badgeText && styleUsesLocationBadge(styleKey)) {
      await drawLocationBadge(ctx, size, badgeText, attribution.topY);
    }

    const blob = await new Promise<Blob | null>((resolve) =>
      out.toBlob(resolve, format === 'jpeg' ? 'image/jpeg' : 'image/png', quality),
    );
    if (!blob) throw new Error('toBlob returned null');
    return blob;
  } finally {
    map?.remove();
    host.remove();
    if (dprDescriptor) Object.defineProperty(window, 'devicePixelRatio', dprDescriptor);
    await raf(); // let the restored DPR settle before anything else paints
  }
}

/** Convenience: render and hand back an object URL plus its disposer. */
export async function renderMapImageObjectUrl(
  opts: RenderMapImageOptions,
): Promise<{ url: string; revoke: () => void }> {
  const blob = await renderMapImage(opts);
  const url = URL.createObjectURL(blob);
  return { url, revoke: () => URL.revokeObjectURL(url) };
}
